/**
 * NIRSALIntelligencePanel — National Program Intelligence Layer
 * Mounted on /corporate/nirsal/dashboard below existing KPI strip.
 *
 * 4 panels:
 *   1. National Scale & Projection (low/base/high, non-linear S-curve)
 *   2. Prioritization Engine (8-dimension ranked table)
 *   3. Article 6.4 Strategic View
 *   4. Capital Deployment Strategy
 *
 * Data: GET /api/nirsal/national-intelligence/{period_id}
 * All values clearly labelled Indicative / Modeled.
 */
import React, { useEffect, useState } from "react";
import { SectionCard } from "@/components/ds";
import client from "@/api/client";
import { TrendingUp, Target, Zap, Shield, Clock, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmtM = (v, dec = 1) => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e9) return `${(n / 1e9).toFixed(dec)}B`; if (n >= 1e6) return `${(n / 1e6).toFixed(dec)}M`; if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`; return `${n}`; };
const fmtU = (v) => { if (!v) return "—"; const n = Number(v); if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`; if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n}`; };
const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : "—";

// ── Sub-components ─────────────────────────────────────────────────────────────
const DisclaimerBanner = () => (
  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
    <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
    <p className="text-xs text-amber-700">
      <strong>Indicative projections only.</strong> All scale and carbon values are modeled outputs from a non-linear S-curve adoption model applied to the SSOT pilot baseline. Not forecasts or investment commitments. Carbon values conditional on VVB validation, DOE engagement, and NCCC authorization.
    </p>
  </div>
);

const ScoreBar = ({ value, max = 100, color = "#1e3a5f" }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${(value / max) * 100}%`, background: color }} />
    </div>
    <span className="text-[10px] text-slate-500 w-6 text-right">{value}</span>
  </div>
);

const PhaseChip = ({ label, color }) => (
  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: color + "18", color }}>{label}</span>
);

// ── Panel 1: Scale Model ───────────────────────────────────────────────────────
function ScalePanel({ scalePT, techProjections }) {
  const phases = [
    { key: "pilot",       label: "Pilot",           color: "#64748b", desc: "Current 5 clusters" },
    { key: "early_scale", label: "Early Scale",      color: "#d97706", desc: "5–10 states" },
    { key: "national",    label: "National",         color: "#059669", desc: "37 states + FCT" },
  ];

  return (
    <SectionCard title="Scale Projection — Non-linear S-Curve Model">
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          {phases.map(ph => {
            const d = scalePT[ph.key] || {};
            const isBase = ph.key === "pilot";
            return (
              <div key={ph.key} className="rounded-xl p-4 border" style={{ background: ph.color + "08", borderColor: ph.color + "33" }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: ph.color }} />
                  <p className="text-xs font-black" style={{ color: ph.color }}>{ph.label}</p>
                </div>
                <p className="text-[10px] text-slate-400 mb-3">{ph.desc}</p>
                {isBase ? (
                  <div>
                    <p className="text-xs text-slate-500">Reduction</p>
                    <p className="text-lg font-black text-slate-900">{fmtM(d.red_base)}<span className="text-xs font-normal text-slate-400"> tCO₂e/yr</span></p>
                    <p className="text-xs text-slate-500 mt-1">Capital deployed</p>
                    <p className="text-sm font-bold text-slate-700">{fmtU(d.cap_base)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-slate-500">Reduction (low / base / high)</p>
                    <p className="text-sm font-black text-slate-900">
                      <span className="text-slate-400">{fmtM(d.red_low)}</span>
                      {" / "}
                      <span style={{ color: ph.color }}>{fmtM(d.red_base)}</span>
                      {" / "}
                      <span className="text-slate-400">{fmtM(d.red_high)}</span>
                      <span className="text-xs font-normal text-slate-400"> tCO₂e/yr</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1.5">Capital required</p>
                    <p className="text-xs font-bold text-slate-600">{fmtU(d.cap_low)} – {fmtU(d.cap_high)}</p>
                    <p className="text-xs text-slate-500 mt-1">CRG exposure</p>
                    <p className="text-xs font-bold text-purple-700">{fmtU(d.crg_base)}</p>
                    {d.carbon_value_base && (
                      <>
                        <p className="text-xs text-slate-500 mt-1">Carbon value (base)</p>
                        <p className="text-xs font-bold text-emerald-700">{fmtU(d.carbon_value_base)}/yr indicative</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Per-technology scale bar */}
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">National Scale Potential by Technology</p>
        <div className="space-y-2">
          {techProjections.map(tp => {
            const nat = tp.phases?.national || {};
            const maxRed = Math.max(...techProjections.map(t => t.phases?.national?.red_base || 0));
            const pctShare = maxRed > 0 ? (nat.red_base || 0) / maxRed : 0;
            return (
              <div key={tp.technology} className="flex items-center gap-2">
                <p className="text-[10px] text-slate-600 w-44 flex-shrink-0 truncate">{tp.label}</p>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#1e3a5f]" style={{ width: `${pctShare * 100}%`, opacity: tp.art6_4 ? 1 : 0.6 }} />
                </div>
                <p className="text-[10px] text-slate-500 w-20 text-right">{fmtM(nat.red_base)} tCO₂e</p>
                {tp.art6_4 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold flex-shrink-0">Art.6.4</span>}
              </div>
            );
          })}
        </div>
        <p className="text-[9px] text-slate-400 mt-3 italic">Modeled using non-linear S-curve adoption model. Higher k-saturation = more diminishing returns at scale. All values indicative.</p>
      </div>
    </SectionCard>
  );
}

// ── Panel 2: Prioritization Engine ────────────────────────────────────────────
function PrioritizationPanel({ prioritization }) {
  const tagColors = {
    "Scale immediately":        { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" },
    "Pilot first":              { bg: "#fffbeb", text: "#d97706", border: "#fde68a" },
    "Structure before scaling": { bg: "#f5f3ff", text: "#7c3aed", border: "#ddd6fe" },
    "Not priority":             { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" },
  };

  const DIM_LABELS = [
    ["reduction", "Reduction Potential"],
    ["cap_eff",   "Capital Efficiency"],
    ["crg_fit",   "CRG Fit"],
    ["pfi_appetite","PFI Appetite"],
    ["mrv",       "MRV Readiness"],
    ["art6_4",    "Art.6.4 Eligibility"],
    ["speed",     "Speed"],
    ["rural",     "Rural Impact"],
  ];

  return (
    <SectionCard title="Prioritization Engine — 8-Dimension Scoring">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#0f2d5c] text-white">
              <th className="text-left py-2 px-3 font-medium">#</th>
              <th className="text-left py-2 px-3 font-medium">Intervention</th>
              <th className="text-left py-2 px-3 font-medium">Pathway</th>
              <th className="text-left py-2 px-3 font-medium w-[180px]">Score Breakdown</th>
              <th className="text-right py-2 px-3 font-medium">Composite</th>
              <th className="text-left py-2 px-3 font-medium">Recommendation</th>
              <th className="text-right py-2 px-3 font-medium">Nat. Scale</th>
              <th className="text-right py-2 px-3 font-medium">Speed</th>
            </tr>
          </thead>
          <tbody>
            {prioritization.map((row, i) => {
              const tc = tagColors[row.recommendation] || tagColors["Not priority"];
              const dims = row.dimensions || {};
              return (
                <tr key={row.technology} className="border-b border-slate-50 hover:bg-slate-50/80" data-testid={`priority-row-${i}`}>
                  <td className="py-3 px-3 font-black text-[#1e3a5f] text-base">{row.rank}</td>
                  <td className="py-3 px-3">
                    <p className="font-semibold text-slate-800">{row.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{row.priority_states?.slice(0,3).join(", ")}</p>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${row.art6_4_eligible ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {row.art6_4_eligible ? "Art.6.4" : row.pathway === "plastic" ? "Plastic" : "VCM"}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <div className="space-y-0.5 w-44">
                      {DIM_LABELS.map(([k, lbl]) => (
                        <div key={k} className="flex items-center gap-1.5">
                          <span className="text-[8px] text-slate-400 w-20 flex-shrink-0 truncate">{lbl}</span>
                          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-[#1e3a5f]" style={{ width: `${dims[k] || 0}%`, opacity: 0.7 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span className="text-lg font-black text-[#1e3a5f]">{row.composite_score}</span>
                    <span className="text-[10px] text-slate-400">/100</span>
                  </td>
                  <td className="py-3 px-3">
                    <span className="text-[10px] font-bold px-2 py-1 rounded-lg border" style={{ background: tc.bg, color: tc.text, borderColor: tc.border }}>
                      {row.recommendation}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right text-emerald-700 font-bold">
                    {fmtM(row.nat_red_base)}<br />
                    <span className="text-[9px] text-slate-400 font-normal">tCO₂e/yr</span>
                  </td>
                  <td className="py-3 px-3 text-right text-slate-500">
                    {row.speed_months}mo
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
        <p className="text-[10px] text-slate-400">
          Scoring weights: Reduction Potential 25% · Capital Efficiency 15% · CRG Fit 15% · PFI Appetite 10% · MRV Readiness 10% · Art.6.4 Eligibility 10% · Speed 10% · Rural Impact 5%
        </p>
      </div>
    </SectionCard>
  );
}

// ── Panel 3: Article 6.4 Strategic View ───────────────────────────────────────
function Art64Panel({ art64 }) {
  const ns = art64?.national_scale || {};
  const timeline = art64?.timeline || [];

  return (
    <SectionCard title="Article 6.4 Program Potential">
      <div className="p-4">
        <div className="bg-[#0f2d5c]/5 border border-[#0f2d5c]/15 rounded-xl p-4 mb-4">
          <p className="text-xs font-black text-[#1e3a5f] mb-3">Indicative ITMO Generation Potential at National Scale</p>
          <div className="grid grid-cols-3 gap-4">
            {[["Low", ns.low, "#64748b"],["Base", ns.base, "#1e3a5f"],["High", ns.high, "#059669"]].map(([l,v,c]) => (
              <div key={l} className="text-center">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">{l}</p>
                <p className="text-2xl font-black" style={{color:c}}>{fmtM(v)}</p>
                <p className="text-[10px] text-slate-400">tCO₂e/yr indicative</p>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">% of national portfolio</span><span className="font-bold text-[#1e3a5f]">{pct(art64?.portfolio_art6_pct)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Carbon value (base)</span><span className="font-bold text-emerald-700">{fmtU(art64?.carbon_value_base_usd)}/yr indicative</span></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Registration to Issuance Timeline</p>
            <div className="space-y-2">
              {timeline.map((t, i) => (
                <div key={i} className="flex gap-2">
                  <div className="flex flex-col items-center">
                    <div className="w-5 h-5 rounded-full bg-[#1e3a5f] text-white text-[9px] flex items-center justify-center font-bold flex-shrink-0">{i+1}</div>
                    {i < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" style={{minHeight:"12px"}} />}
                  </div>
                  <div className="pb-2">
                    <p className="text-[10px] font-bold text-slate-700">{t.phase}</p>
                    <p className="text-[9px] text-blue-700 font-medium">Month {t.months}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5">{t.actions}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Key Risks</p>
            <div className="space-y-2">
              {(art64?.key_risks || []).map((r, i) => (
                <div key={i} className="flex gap-1.5 text-[10px] text-slate-600">
                  <span className="text-amber-500 flex-shrink-0">▲</span>
                  <p>{r}</p>
                </div>
              ))}
            </div>
            <p className="text-[9px] text-red-600 mt-3 p-2 bg-red-50 rounded border border-red-100">
              {art64?.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ── Panel 4: Capital Deployment Strategy ──────────────────────────────────────
function CapitalPanel({ capitalStrategy }) {
  const byPhase = capitalStrategy?.by_phase || [];
  const byTech  = capitalStrategy?.by_technology || [];

  const PRIORITY_COLORS = { HIGH: "#059669", MEDIUM: "#d97706", LOW: "#64748b" };

  return (
    <SectionCard title="Capital Deployment Strategy — Where to Focus Guarantees First">
      <div className="p-4">
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs font-bold text-emerald-800">{capitalStrategy?.guarantee_focus}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {/* Phase deployment */}
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Capital by Deployment Phase</p>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100">
                {["Phase","Capital","CRG","Leverage"].map(h => <th key={h} className="text-left py-1.5 px-2 text-slate-400 font-medium text-[10px]">{h}</th>)}
              </tr></thead>
              <tbody>
                {byPhase.map(ph => (
                  <tr key={ph.phase} className="border-b border-slate-50">
                    <td className="py-1.5 px-2 font-medium">{ph.label}</td>
                    <td className="py-1.5 px-2 text-slate-700">{fmtU(ph.capital_base)}</td>
                    <td className="py-1.5 px-2 text-purple-700">{fmtU(ph.crg_base)}</td>
                    <td className="py-1.5 px-2 font-bold text-[#1e3a5f]">{ph.leverage}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-technology guarantee priority */}
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Guarantee Priority by Technology</p>
            <div className="space-y-2">
              {byTech.slice(0,5).map(t => {
                const pc = PRIORITY_COLORS[t.guarantee_priority] || "#64748b";
                return (
                  <div key={t.technology} className="flex items-center gap-2">
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded" style={{ background: pc + "18", color: pc }}>{t.guarantee_priority}</span>
                    <div className="flex-1">
                      <p className="text-[10px] font-medium text-slate-700 truncate">{t.label}</p>
                      <p className="text-[9px] text-slate-400">{t.recommendation} · {t.leverage_ratio}x leverage</p>
                    </div>
                    <p className="text-[10px] text-purple-700 font-bold flex-shrink-0">{fmtU(t.nat_crg_base)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* PFI segments */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">PFI Participation Potential</p>
          <div className="grid grid-cols-4 gap-3">
            {(capitalStrategy?.pfi_segments || []).map(seg => (
              <div key={seg.segment} className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-bold text-slate-700">{seg.segment}</p>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${seg.appetite === "High" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{seg.appetite} appetite</span>
                <p className="text-[9px] text-slate-500 mt-1">{seg.focus}</p>
                <p className="text-[9px] text-blue-600 font-medium mt-1">{seg.typical_ticket}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[9px] text-slate-400 mt-3 italic">{capitalStrategy?.disclaimer}</p>
      </div>
    </SectionCard>
  );
}

// ── Assumption Panel ─────────────────────────────────────────────────────────
function AssumptionPanel({ assumptions, sensitivityParams, activeSens }) {
  const [open, setOpen] = useState(false);
  const sp = sensitivityParams || {};

  return (
    <SectionCard title="Model Assumptions & Constraints">
      <div className="p-4">
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between text-xs font-semibold text-[#1e3a5f] py-2 hover:text-blue-700 transition-colors"
          data-testid="toggle-assumptions"
        >
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-[#1e3a5f]/10 flex items-center justify-center text-[10px]">
              {open ? "▲" : "▼"}
            </span>
            {open ? "Hide" : "Show"} per-intervention assumptions ({assumptions?.length || 0} interventions)
          </span>
          <span className="text-[10px] text-slate-400 font-normal">Source: NIRSAL Concept Paper, FAO Nigeria, CBN Reports</span>
        </button>

        {/* Sensitivity assumption descriptions */}
        <div className="mb-3 p-3 rounded-lg border" style={{
          background: activeSens === "conservative" ? "#fffbeb" : activeSens === "aggressive" ? "#ecfdf5" : "#f0f9ff",
          borderColor: activeSens === "conservative" ? "#fde68a" : activeSens === "aggressive" ? "#a7f3d0" : "#bae6fd",
        }}>
          <p className="text-[10px] font-bold mb-1.5" style={{ color: activeSens === "conservative" ? "#d97706" : activeSens === "aggressive" ? "#059669" : "#0284c7" }}>
            {sp[activeSens]?.label} Scenario — Key Assumptions
          </p>
          <ul className="space-y-0.5">
            {(sp[activeSens]?.assumptions || []).map((a, i) => (
              <li key={i} className="text-[10px] text-slate-600 flex gap-1.5">
                <span className="text-slate-400 flex-shrink-0">•</span>{a}
              </li>
            ))}
          </ul>
        </div>

        {open && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-[#0f2d5c] text-white">
                  {["Intervention", "Max Clusters", "Capital/Cluster", "CRG Fit", "MRV Feasibility", "Art.6.4", "Speed", "Saturation", "Methodology"].map(h => (
                    <th key={h} className="text-left py-2 px-2 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(assumptions || []).map((a, i) => (
                  <tr key={a.technology} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-medium text-slate-800">{a.label}</td>
                    <td className="py-2 px-2 text-slate-600">{a.max_clusters?.toLocaleString()}</td>
                    <td className="py-2 px-2">${(a.capital_per_cluster_base / 1000).toFixed(0)}K <span className="text-slate-400">±{a.capital_variance_pct}%</span></td>
                    <td className="py-2 px-2">
                      <span className={`px-1.5 py-0.5 rounded font-bold ${a.crg_suitability_pct >= 80 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {a.crg_suitability_pct}%
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`px-1.5 py-0.5 rounded font-bold ${a.mrv_feasibility_pct >= 80 ? "bg-emerald-100 text-emerald-700" : a.mrv_feasibility_pct >= 65 ? "bg-amber-100 text-amber-700" : "bg-red-50 text-red-600"}`}>
                        {a.mrv_feasibility_pct}%
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      {a.art6_4_eligible ? <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">Yes</span> : <span className="text-slate-400">No</span>}
                    </td>
                    <td className="py-2 px-2 text-slate-600">{a.speed_months_base}mo</td>
                    <td className="py-2 px-2">
                      <span className="text-slate-500">{a.saturation_k} </span>
                      <span className="text-[9px] text-slate-400">({a.saturation_label?.split(" ")[0]})</span>
                    </td>
                    <td className="py-2 px-2 text-slate-400 max-w-[120px] truncate" title={a.methodology}>{a.methodology}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 p-2 bg-slate-50 rounded text-[9px] text-slate-400">
              <strong>Sources:</strong> FAO Nigeria Agricultural Production Statistics 2023 · NIRSAL Green Credit Concept Paper (2024) · CBN Development Finance Reports 2022–2024 · IPCC 2006 Vol.5 emission factors · Carbon price: ${sp[activeSens]?.carbon_price_t}/t ({sp[activeSens]?.label} scenario)
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// ── Key Risks Panel ───────────────────────────────────────────────────────────
function KeyRisksPanel({ keyRisks }) {
  const SEVERITY_STYLE = {
    High:   { bg: "#fee2e2", text: "#dc2626", border: "#fca5a5" },
    Medium: { bg: "#fffbeb", text: "#d97706", border: "#fde68a" },
    Low:    { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
  };

  return (
    <SectionCard title="Key Risk Flags">
      <div className="p-4 space-y-3">
        {(keyRisks || []).map((r, i) => {
          const s = SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.Medium;
          return (
            <div key={i} className="rounded-lg border p-3" style={{ borderColor: s.border, background: s.bg + "40" }} data-testid={`risk-row-${i}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-xs font-bold text-slate-800">{r.category}</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0" style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>{r.severity}</span>
              </div>
              <p className="text-xs text-slate-600 mb-1.5">{r.risk}</p>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="bg-white/60 rounded p-1.5">
                  <span className="text-slate-400 font-medium">Model driver: </span>
                  <span className="text-slate-600">{r.driver}</span>
                </div>
                <div className="bg-white/60 rounded p-1.5">
                  <span className="text-slate-400 font-medium">Mitigation: </span>
                  <span className="text-slate-600">{r.mitigation}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ── Main exported component ────────────────────────────────────────────────────
export default function NIRSALIntelligencePanel({ selectedPeriod }) {
  const [sensitivity, setSensitivity] = useState("base");
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [sensParams, setSensParams] = useState(null);

  // Load assumptions/sensitivity params once
  useEffect(() => {
    client.get("/nirsal/model-assumptions")
      .then(r => setSensParams(r.data.sensitivity_params))
      .catch(() => {});
  }, []);

  // Re-fetch whenever period or sensitivity changes
  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    setError(null);
    client.get(`/nirsal/national-intelligence/${selectedPeriod}?sensitivity=${sensitivity}`)
      .then(r => setData(r.data))
      .catch(() => setError("Intelligence layer unavailable"))
      .finally(() => setLoading(false));
  }, [selectedPeriod, sensitivity]);

  if (loading) return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-8 text-center">
      <div className="w-6 h-6 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-xs text-slate-400">Computing national intelligence ({sensitivity})…</p>
    </div>
  );
  if (error) return (
    <div className="bg-white rounded-xl border border-red-100 p-6 text-center">
      <p className="text-xs text-red-500">{error}</p>
    </div>
  );
  if (!data) return null;

  const scale      = data.scale_model || {};
  const prio       = data.prioritization || [];
  const art64      = data.art64_strategy || {};
  const cap        = data.capital_strategy || {};
  const assumptions= data.assumptions || [];
  const keyRisks   = data.key_risks || [];
  const meta       = data.meta || {};

  const SENSITIVITY_OPTIONS = [
    { value: "conservative", label: "Conservative", color: "#d97706", bg: "bg-amber-50 border-amber-200 text-amber-700" },
    { value: "base",         label: "Base",          color: "#1e3a5f", bg: "bg-blue-50 border-blue-200 text-blue-700"   },
    { value: "aggressive",   label: "Aggressive",    color: "#059669", bg: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  ];

  return (
    <div className="space-y-5" data-testid="national-intelligence-panel">
      {/* Panel header + Sensitivity toggle */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-6 rounded-full bg-[#1e3a5f]" />
            <h2 className="text-sm font-black text-[#1e3a5f]">National Program Scale & Prioritization</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-bold">Indicative — Modeled</span>
          </div>
          <p className="text-xs text-slate-400 ml-3.5 mt-0.5">Non-linear S-curve projections · 8-dimension prioritization · Art.6.4 strategy · Capital deployment</p>
          {meta.traceability && (
            <div className="ml-3.5 mt-1 text-[9px] text-slate-400 space-y-0.5">
              <p><span className="font-medium">Scale driver:</span> {meta.traceability.scale_driver}</p>
              <p><span className="font-medium">Carbon driver:</span> {meta.traceability.carbon_driver}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="text-[10px] px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg font-bold">
            NIRSAL — Institutionally Defensible Climate Finance Platform
          </span>
          {/* Sensitivity toggle */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1" data-testid="sensitivity-toggle">
            <span className="text-[10px] text-slate-400 px-1">Scenario:</span>
            {SENSITIVITY_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setSensitivity(opt.value)}
                data-testid={`sensitivity-${opt.value}`}
                className={`text-[10px] px-3 py-1.5 rounded-lg border font-bold transition-all ${sensitivity === opt.value ? opt.bg : "text-slate-400 border-transparent hover:bg-slate-50"}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {sensParams && (
            <p className="text-[9px] text-slate-400 text-right max-w-xs">{sensParams[sensitivity]?.description}</p>
          )}
        </div>
      </div>

      <DisclaimerBanner />

      {/* Assumption Panel — BEFORE scale outputs for traceability */}
      <AssumptionPanel assumptions={assumptions} sensitivityParams={sensParams} activeSens={sensitivity} />

      <ScalePanel    scalePT={scale.portfolio_totals || {}} techProjections={scale.tech_projections || []} />
      <PrioritizationPanel prioritization={prio} />
      <Art64Panel    art64={art64} />
      <CapitalPanel  capitalStrategy={cap} />

      {/* Key Risks — AFTER main panels */}
      <KeyRisksPanel keyRisks={keyRisks} />
    </div>
  );
}
